import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Sparkles,
  XCircle,
  Calendar,
  ShieldCheck,
  Play,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useRef, useState } from 'react';
import SEO from '../components/SEO';
import { TermsNote } from '../components/TermsNote';
import { STRIPE_CHECKOUT_URL, DISCOVERY_CALL_URL, BUNDLE_PRICE, BUNDLE_PRICE_STRIKE, BUNDLE_SAVINGS, BUNDLE_CHECKOUT_URL, BUNDLE_CHECKOUT_READY, PAYMENT_OPTIONS_SHORT } from '../constants/site';
import { ORGANIZATION_SCHEMA, COURSE_SCHEMA, FAQ_SCHEMA } from '../constants/seo';
import { LIVE_SESSIONS } from '../constants/cohorts';

const ACADEMY_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [ORGANIZATION_SCHEMA, COURSE_SCHEMA, FAQ_SCHEMA],
};

const INTRO_VIDEO_URL =
  'https://customer-assets-eiarnc6j.emergentagent.net/job_gate33-preview/artifacts/u2fqwcc5_5e122b00d3464433b90f3ab74918392a.MP4';
const INTRO_VIDEO_POSTER =
  'https://customer-assets-eiarnc6j.emergentagent.net/job_gate33-preview/artifacts/6qvt6asa_9D16A7EE-AFFA-47C3-BE98-8BC98C89E7C9.PNG';

const NEXT_LIVE = LIVE_SESSIONS.find((s) => s.status !== 'full') || LIVE_SESSIONS[0];
const SEATS_REMAINING = NEXT_LIVE.status === 'last' ? '2 seats left' : 'limited seats';
const STATUS_LABEL = { full: 'FULL', last: 'LAST SEATS', open: 'OPEN' };

// ===== Recognition — "Does this sound like you?" =====
const RECOGNITION_PROFILES = [
  { label: 'Commercial crew', body: 'Airline background, ready to leave the roster life.' },
  { label: 'Hospitality professional', body: 'Hotels, fine dining, private households, yachts.' },
  { label: 'Career changer', body: 'Service background, no aviation — but the right instinct.' },
];
const RECOGNITION_LINES = [
  'You apply and never hear back.',
  "You don't know what operators actually screen for.",
  "Your CV says \u201cVIP service\u201d but it isn\u2019t landing interviews.",
  'You\u2019ve been told to "get experience" without being told how.',
];

// ===== Who this is for / not for =====
const IS_FOR = [
  { title: 'Commercial crew', body: 'From 5-star airlines ready to move into business aviation.' },
  { title: 'Hospitality professionals', body: 'From luxury hotels, fine dining, private households, or yachts.' },
  { title: 'Career changers', body: 'With a serious service background and the discipline to run something alone.' },
];
const IS_NOT_FOR = [
  'Anyone expecting a guaranteed job — no training guarantees placement.',
  "People wanting plating technique classes — every operator has its own standard.",
  'Pilots — this is cabin-side operational training only.',
];

// ===== Hour-by-hour agenda =====
const AGENDA = [
  {
    day: 'Day 1',
    eyebrow: 'Foundation & Operational Awareness',
    blocks: [
      {
        time: '09:00 — 10:30',
        title: '01 · Introduction to Private Aviation',
        outcome: 'You can describe the operator landscape (charter, corporate, owner, fractional) and know where your profile fits — live Q&A.',
      },
      {
        time: '10:45 — 12:15',
        title: '02 · Aircraft Types & Cabin Environments',
        outcome: 'You can name the working differences between Bombardier, Gulfstream, Falcon, ACJ and BBJ galleys — live Q&A.',
      },
      {
        time: '13:15 — 14:45',
        title: '03 · Cabin Attendant Structures',
        outcome: 'You understand how hierarchies, roles and single-attendant reality work across premium operators — live Q&A.',
      },
      {
        time: '15:00 — 16:15',
        title: '04 · UHNW Clients & Cultural Awareness',
        outcome: 'You can read a principal brief and adjust your approach to cultural codes and discretion — live Q&A.',
      },
      {
        time: '16:30 — 17:30',
        title: '05 · Cabin Safety & Pilot Relationship',
        outcome: 'You know cabin secure procedure and how to work with the flight deck as the only cabin attendant — live Q&A.',
      },
      {
        time: '17:30 — 18:15',
        title: '06 · Positioning & Profile Building',
        outcome: 'Your CV, LinkedIn and narrative are repositioned in operator language before Day 2 — live Q&A.',
      },
    ],
  },
  {
    day: 'Day 2',
    eyebrow: 'Service, Execution & Recovery',
    blocks: [
      {
        time: '09:00 — 10:45',
        title: '07 · Catering Intelligence & Operational Planning',
        outcome: 'You can place a real catering order for a real flight — suppliers, budgets, sequence, galley constraints — live practice with feedback.',
      },
      {
        time: '11:00 — 12:30',
        title: '08 · Plating & Presentation',
        outcome: 'You understand the standard operators screen for — without pretending months of plating training in one session — live walkthroughs.',
      },
      {
        time: '13:30 — 15:00',
        title: '09 · Wine, Spirits & Barista Service',
        outcome: 'You know what to master, what to skip, and how to hold a service standard in the cabin — live Q&A.',
      },
      {
        time: '15:15 — 17:30',
        title: '10 · Crisis Handling & Service Recovery',
        outcome: 'You can walk through inflight resolution and client communication when the plan breaks — live case work.',
      },
    ],
  },
  {
    day: 'Day 3',
    eyebrow: 'Aircraft Mastery, Crisis Readiness & Interview',
    blocks: [
      {
        time: '09:00 — 10:45',
        title: '11 · Aircraft Deep Dive — Global, Challenger, Gulfstream, Falcon',
        outcome: 'You know what flying each type actually looks like day-to-day, taught from live corporate operations — live Q&A.',
      },
      {
        time: '11:00 — 12:30',
        title: '12 · Rotation Reality — Multi-Sector Operations',
        outcome: 'You understand stock control, FBO coordination, post-flight reporting — the 80% of the job that is not service — live Q&A.',
      },
      {
        time: '13:30 — 15:30',
        title: '13 · Interview Preparation',
        outcome: 'You have practised operator interview mechanics on real questions — live mock and feedback.',
      },
      {
        time: '15:45 — 17:30',
        title: '14 · Final Debrief & Placement Roadmap',
        outcome: 'You leave with your operator shortlist, positioning plan and next steps — live 1:1 debrief.',
      },
    ],
  },
];

// ===== Numbered outcomes =====
const OUTCOMES = [
  {
    n: '01',
    title: 'A repositioned profile operators actually recognise',
    body:
      'Your CV, LinkedIn and narrative rewritten in operator language — evidence of autonomy, discretion and judgement, not "VIP service".',
  },
  {
    n: '02',
    title: 'A working understanding of catering sourcing and provisioning',
    body:
      'Not plating theatre. The layer that decides whether a full meal on a 55-minute leg is even possible — sequence, sourcing, galley constraints, budgets.',
  },
  {
    n: '03',
    title: 'The ability to handle a flight solo',
    body:
      'No purser above you, no ground team behind you. Cabin flow, safety context, flight-deck relationship — the single-attendant reality of business aviation.',
  },
  {
    n: '04',
    title: 'Interview answers built on operational reality',
    body:
      'Practised on real operator questions. You know what they ask, what they listen for, and how to answer as someone who has already thought about it.',
  },
  {
    n: '05',
    title: 'A clear placement roadmap',
    body:
      'On Day 3 you leave with your operator shortlist, positioning plan and next steps — not a certificate, a direction.',
  },
];

// ===== Text testimonials =====
const TESTIMONIALS = [
  {
    initials: 'AM',
    name: 'Alexandra Mirutoi',
    background: 'Ex-airline · now VVIP Flight Attendant, Saudi Royal Aviation',
    quote:
      'The training goes far beyond service and focuses on the real operational side of business aviation.',
  },
  {
    initials: 'MC',
    name: 'Matteo Chivilo',
    background: 'Cabin Senior — Etihad Airways',
    quote:
      'What stood out was his understanding of what private aviation operators actually look for — beyond a standard airline profile.',
  },
  {
    initials: 'TD',
    name: 'Thomas D.',
    background: 'Cabin Crew — Transavia',
    quote:
      'You get a real grasp of what the industry demands: autonomy, flight organisation, catering, mindset, positioning, recruitment, and operator expectations.',
  },
  {
    initials: 'MT',
    name: 'Marie T.',
    background: 'Premium Flight Attendant — Etihad Airways',
    quote:
      'The operational reality behind the training is heavy, and he explains the real expectations of private aviation.',
  },
];

// ===== Objection FAQ =====
const OBJECTION_FAQ = [
  {
    q: 'Will this get me a job?',
    a: 'Honest answer: no training guarantees placement. Gate 33 makes introductions where there is genuine fit, and the program exists because positioning — not experience — is what blocks most candidates. Placement depends on your positioning, timing and market fit. The training makes you a credible candidate; you still have to show up and apply.',
  },
  {
    q: "I'm not cabin crew — is this for me?",
    a: 'Yes. Operators also hire from luxury hotels, fine dining, private households and yachts. You already have the service instinct; the training gives you the aircraft-side operational reality. On the discovery call we look at exactly how to position your background.',
    discoveryLink: true,
  },
  {
    q: 'Why online and not in person?',
    a: 'Because the training is fully live — three consecutive days, in session with your trainer — and delivered online means no flights, no hotel, no time off beyond the training days themselves. You get the same live delivery without adding travel cost to the fee.',
  },
  {
    q: "What if the dates don't work?",
    a: 'The program can be run one-to-one around a roster, or you can defer your seat to a later cohort free of charge — see the risk reversal below.',
  },
  {
    q: 'Can I pay in instalments?',
    a: 'Yes — €600 in full, or up to 24× payments with PayPal at checkout. Same content, same live sessions.',
  },
  {
    q: 'Do you teach service and plating?',
    a: 'Not as a technical class, no. Every operator has its own plating standard, and plating mastery takes months of live flying to acquire in operator context. It is also not what operators screen for. We cover it in the sense of standards and sequence — enough to hold your own in interview and on trial flights — but the training deliberately spends its time on the 80% of the job that is not service.',
  },
];

const AcademyPage = () => {
  const introVideoRef = useRef(null);
  const [introStarted, setIntroStarted] = useState(false);
  const [introMuted, setIntroMuted] = useState(false);

  const startIntroVideo = () => {
    const v = introVideoRef.current;
    if (!v) return;
    v.muted = false;
    v.currentTime = 0;
    v.play().catch(() => {
      // If autoplay-with-sound is blocked, fall back to muted playback
      v.muted = true;
      setIntroMuted(true);
      v.play().catch(() => {});
    });
    setIntroStarted(true);
  };

  const toggleIntroSound = () => {
    const v = introVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIntroMuted(v.muted);
  };

  return (
  <div data-testid="academy-page" className="min-h-screen">
    <SEO
      title="3-Day Online Training — Break Into Private Aviation | Gate 33"
      description="3 live online days with a working corporate flight attendant. Built for commercial crew, hospitality professionals and career changers. €600 or up to 24× payments with PayPal. Reserve your seat."
      path="/academy"
      keywords="VIP Flight Attendant Training France, Private Jet Flight Attendant Training, VIP Cabin Crew Training Europe, Corporate Flight Attendant Training, Private Aviation Training Online, Business Aviation Cabin Crew Course"
      jsonLd={ACADEMY_JSONLD}
    />

    {/* ============================================================ */}
    {/* HERO — outcome-led                                            */}
    {/* ============================================================ */}
    <section data-testid="academy-hero" className="relative min-h-[85vh] flex items-center">
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1661954864180-e61dea14208a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxwcml2YXRlJTIwamV0JTIwZ2FsbGV5JTIwc2VydmljZSUyMGNhdGVyaW5nJTIwcHJlcGFyYXRpb258ZW58MHx8fHwxNzc1MDcwMTMwfDA&ixlib=rb-4.1.0&q=85"
          alt="Gate 33 3-Day Online Training — private aviation preparation from anywhere"
          title="Gate 33 3-Day Online Training"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/95 to-[#FDFBF7]/50" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-24 w-full text-center">
        <div className="max-w-3xl mx-auto">
          <span className="overline inline-block mb-6">3-Day Online Training · Live</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
              Become the candidate operators actually hire.
            </h1>
            <p className="text-lg text-[#1A1A1A] leading-relaxed mb-6">
              3 live online days with a working corporate flight attendant. An <span className="font-semibold">operational training</span> — because <span className="font-semibold">80% of the job is operations, not service</span>. Built for commercial crew, hospitality professionals and career changers.
            </p>

            {/* Instructor credential */}
            <div data-testid="academy-hero-trainer" className="flex items-center gap-4 mb-8 max-w-xl mx-auto text-left">
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_gate33-preview/artifacts/dq20hpml_Screenshot%202026-07-18%20at%2020.51.57.png"
                alt="Nicolas Perot dos Santos — Founder of Gate 33"
                className="w-14 h-14 rounded-full object-cover object-center border-2 border-[#C5A880] flex-shrink-0"
              />
              <p className="text-xs sm:text-sm text-[#1A1A1A] leading-snug">
                <span className="font-semibold">Nicolas Perot dos Santos</span> — Qatar Airways, Etihad, today flying <span className="font-semibold">Bombardier Global &amp; Challenger series</span>.
              </p>
            </div>

            {/* Next cohort */}
            <div data-testid="academy-hero-next-live" className="inline-flex items-center gap-2 border border-[#C5A880] bg-[#FDFBF7] px-4 py-2 mb-8">
              <Calendar size={14} className="text-[#C5A880]" strokeWidth={1.8} />
              <span className="text-xs uppercase tracking-[0.18em] text-[#0F0F0F] font-semibold">
                Next cohort · {NEXT_LIVE.dates} · {SEATS_REMAINING}
              </span>
            </div>

            <div className="flex flex-wrap justify-center gap-4 items-center">
              <a
                href={DISCOVERY_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="academy-hero-cta"
                onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'academy_hero' })}
                className="btn-primary inline-flex items-center gap-2"
              >
                Book a Free Discovery Call <ArrowRight size={16} />
              </a>
              <a
                href={STRIPE_CHECKOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="academy-hero-reserve-cta"
                onClick={() => window.gtag?.('event', 'checkout_clicked', { location: 'academy_hero' })}
                className="btn-secondary inline-flex items-center gap-2"
              >
                Reserve my seat <ArrowRight size={16} />
              </a>
            </div>
            <p className="text-xs text-[#5A5A5A] mt-3">€600 · or up to 24× with PayPal · No obligation call, 45 minutes with Nicolas</p>
            <TermsNote testId="academy-hero-terms-note" className="mt-1" />
        </div>
      </div>
    </section>

    {/* proof-after-hero removed — Alexandra quote moved into "In their words" section */}

    {/* ============================================================ */}
    {/* WHAT GATE 33 IS — intro + click-to-play video                 */}
    {/* ============================================================ */}
    {/* ============================================================ */}
    {/* RECOGNITION — Does this sound like you?                       */}
    {/* ============================================================ */}
    <section data-testid="academy-recognition" className="py-16 md:py-20 bg-[#FDFBF7]">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-12">
          <span className="overline inline-block mb-4">Does this sound like you?</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#0F0F0F]">
            You are not alone — this is exactly the room this training was built for.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {RECOGNITION_PROFILES.map((p) => (
            <div
              key={p.label}
              data-testid={`academy-recognition-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="bg-white border border-[#E5E0D8] p-6"
            >
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold">{p.label}</span>
              <p className="mt-2 text-sm text-[#1A1A1A] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>

        <ul className="space-y-3 mb-8">
          {RECOGNITION_LINES.map((line) => (
            <li key={line} className="flex items-start gap-3 text-[#1A1A1A] leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C5A880] mt-2.5 flex-shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <p className="font-heading italic text-xl text-[#0F0F0F] leading-relaxed border-l-2 border-[#C5A880] pl-5">
          That was me, 120 applications ago.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link
            to="/insights/field-notes/120-applications"
            data-testid="academy-recognition-story-link"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#C5A880] font-semibold hover:text-[#0F0F0F] transition-colors"
          >
            Read the full story <ArrowRight size={12} />
          </Link>
          <span className="hidden sm:inline text-[#E5E0D8]">·</span>
          <a
            href={DISCOVERY_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="academy-recognition-discovery-link"
            onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'academy_recognition' })}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#C5A880] font-semibold hover:text-[#0F0F0F] transition-colors"
          >
            Not sure where you stand? Book a free discovery call <ArrowRight size={12} />
          </a>
        </div>
      </div>
    </section>

    <section data-testid="academy-what-gate33" className="py-16 md:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          {/* LEFT — copy */}
          <div className="max-w-2xl">
            <span className="overline inline-block mb-6">What Gate 33 is</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#0F0F0F] mb-6">
              Gate 33 is not a traditional cabin crew school.
            </h2>
            <p className="text-lg text-[#1A1A1A] leading-relaxed mb-4">
              It is a live, three-day <span className="font-semibold">operational training</span> built and delivered by a working corporate flight attendant. Because <span className="font-semibold">80% of this job is operations, not service</span>: catering sourcing, provisioning, sequence design, cabin safety, and the single-attendant discipline that defines business aviation.
            </p>
            <p className="text-[#5A5A5A] leading-relaxed mb-4">
              You already have the service instinct. What operators actually screen for is whether you can run a flight solo — order the catering, sequence a multi-leg rotation, handle the flight deck, coordinate the FBO, recover a service when the plan breaks.
            </p>
            <p className="text-[#5A5A5A] leading-relaxed">
              Three consecutive days in session with Nicolas. No recordings, no self-paced modules, no &ldquo;certificate&rdquo; pretending to replace real operational judgement.
            </p>
          </div>

          {/* RIGHT — click-to-play intro video, same 4:5 frame as hero */}
          <div>
            <div
              data-testid="academy-intro-video-slot"
              className="relative aspect-[4/5] bg-[#0F0F0F] border border-[#C5A880]/30 overflow-hidden shadow-2xl mx-auto max-w-[420px] lg:max-w-none"
            >
              <video
                ref={introVideoRef}
                data-testid="academy-intro-video"
                src={INTRO_VIDEO_URL}
                playsInline
                preload="none"
                poster={INTRO_VIDEO_POSTER}
                className={`absolute inset-0 w-full h-full transition-transform duration-500 ${
                  introStarted ? 'object-cover scale-[1.9] origin-center' : 'object-cover object-top'
                }`}
                onEnded={() => setIntroStarted(false)}
              />

              {/* Play overlay — visible until user clicks */}
              {!introStarted && (
                <button
                  type="button"
                  onClick={startIntroVideo}
                  data-testid="academy-intro-play"
                  aria-label="Play trainer intro video"
                  className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 cursor-pointer group"
                >
                  <span className="absolute inset-0 bg-black/45 group-hover:bg-black/55 transition-colors" aria-hidden="true" />
                  <span className="relative w-20 h-20 rounded-full border-2 border-[#C5A880] flex items-center justify-center mb-5 bg-[#0F0F0F]/60 backdrop-blur transition-transform duration-300 group-hover:scale-110">
                    <Play size={26} className="text-[#C5A880] ml-1" strokeWidth={1.5} />
                  </span>
                  <span className="relative text-[10px] uppercase tracking-[0.25em] text-[#C5A880] font-semibold mb-2">
                    20 sec presentation
                  </span>
                  <span className="relative text-sm text-[#FDFBF7] leading-snug max-w-[16rem]">
                    A short message from your trainer.
                  </span>
                </button>
              )}

              {/* Sound toggle — only once video started */}
              {introStarted && (
                <button
                  type="button"
                  onClick={toggleIntroSound}
                  aria-label={introMuted ? 'Unmute video' : 'Mute video'}
                  data-testid="academy-intro-sound-toggle"
                  className="absolute bottom-3 right-3 w-10 h-10 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur border border-[#C5A880]/50 text-[#FDFBF7] hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-colors z-10"
                >
                  {introMuted ? <VolumeX size={16} strokeWidth={1.6} /> : <Volume2 size={16} strokeWidth={1.6} />}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
    {/* academy-who removed — audience is covered in Recognition section; plating & job answers stay in FAQ */}

    {/* ============================================================ */}
    {/* AGENDA — hour by hour                                          */}
    {/* ============================================================ */}
    {/* ============================================================ */}
    {/* OUTCOMES — numbered                                            */}
    {/* ============================================================ */}
    <section data-testid="academy-outcomes" className="py-16 md:py-20 bg-[#FDFBF7]">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <span className="overline inline-block mb-4">What you leave with</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#0F0F0F]">
            Five concrete things. Written as what you can do, not what you were shown.
          </h2>
        </div>

        <ol className="space-y-5">
          {OUTCOMES.map((o) => (
            <li
              key={o.n}
              data-testid={`academy-outcome-${o.n}`}
              className="bg-white border border-[#E5E0D8] p-7 md:p-8 grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-start"
            >
              <span className="font-heading text-4xl md:text-5xl text-[#C5A880] leading-none">{o.n}</span>
              <div>
                <h3 className="font-heading text-xl text-[#0F0F0F] leading-snug mb-3">{o.title}</h3>
                <p className="text-[#5A5A5A] leading-relaxed">{o.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section data-testid="academy-agenda" className="py-16 md:py-20 bg-[#FDFBF7]">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <span className="overline inline-block mb-4">What your three days look like</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#0F0F0F] mb-4">
            Hour by hour. 14 modules, delivered live.
          </h2>
          <p className="text-[#5A5A5A] leading-relaxed">
            Every block is live with Nicolas — no recordings, no self-paced content. Each session ends with Q&amp;A.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {AGENDA.map((day) => (
            <div
              key={day.day}
              data-testid={`academy-agenda-${day.day.toLowerCase().replace(' ', '-')}`}
              className="bg-white border border-[#E5E0D8] p-7 flex flex-col"
            >
              <div className="pb-5 mb-5 border-b border-[#E5E0D8]">
                <span className="font-heading text-2xl text-[#0F0F0F] block">{day.day}</span>
                <span className="overline text-[#5A5A5A] mt-1 inline-block">{day.eyebrow}</span>
              </div>
              <ul className="space-y-5 flex-grow">
                {day.blocks.map((b) => (
                  <li key={b.title}>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold">
                      {b.time}
                    </p>
                    <p className="font-heading text-base text-[#0F0F0F] mt-1 leading-snug">{b.title}</p>
                    <p className="text-sm text-[#5A5A5A] leading-relaxed mt-2">{b.outcome}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* proof-after-agenda removed — Matteo quote moved into "In their words" section */}
    {/* ============================================================ */}
    {/* PROOF WALL — remaining testimonials                            */}
    {/* ============================================================ */}
    <section data-testid="academy-proof-wall" className="py-16 md:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-12">
          <span className="overline inline-block mb-4">In their words</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#0F0F0F]">
            The reality behind the transition — from people who did it.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TESTIMONIALS.map((t) => (
            <blockquote
              key={t.name}
              data-testid={`academy-testimonial-${t.initials}`}
              className="border border-[#E5E0D8] p-7 bg-[#FDFBF7]"
            >
              <p className="text-[#1A1A1A] leading-relaxed italic mb-5">&ldquo;{t.quote}&rdquo;</p>
              <footer className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#C5A880] text-[#0F0F0F] text-xs font-semibold">
                  {t.initials}
                </span>
                <span className="text-sm">
                  <span className="font-semibold text-[#0F0F0F] block">{t.name}</span>
                  <span className="text-[#5A5A5A]">{t.background}</span>
                </span>
              </footer>
            </blockquote>
          ))}
        </div>

        <p className="mt-10 text-center text-xs uppercase tracking-[0.22em] text-[#5A5A5A] font-semibold">
          Close to 100 candidates trained
        </p>
      </div>
    </section>
    {/* ============================================================ */}
    {/* RISK REVERSAL + PRICING (Training left · Bundle right)         */}
    {/* ============================================================ */}
    <section data-testid="academy-pricing" className="py-16 md:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Risk reversal — full grid width */}
        <div
          data-testid="academy-risk-reversal"
          className="border border-[#C5A880] bg-[#FDFBF7] p-6 md:p-7 mb-8 flex items-start gap-4"
        >
          <ShieldCheck size={22} className="text-[#C5A880] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
          <p className="text-[#1A1A1A] leading-relaxed">
            <span className="font-semibold">Something comes up?</span> Tell us before your cohort starts and we move
            your seat to a later session, free of charge — your roster shouldn&apos;t cost you your place.
          </p>
        </div>

        {/* 2-column grid — Training (light) LEFT · Bundle (dark) RIGHT */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          {/* LEFT — Training pricing card */}
          <div className="border border-[#0F0F0F] p-8 md:p-10 bg-white flex flex-col">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold inline-block mb-4">Training</span>
            <h3 className="font-heading text-2xl sm:text-3xl tracking-tight leading-tight text-[#0F0F0F] mb-6">
              3-Day Online Training
            </h3>
            <div className="flex items-baseline gap-3 mb-2 flex-wrap">
              <span className="font-heading text-5xl text-[#0F0F0F] leading-none">€600</span>
              <span className="text-sm text-[#5A5A5A]">or up to 24× payments with PayPal</span>
            </div>
            <p className="text-xs text-[#5A5A5A] mb-6">One fee. Every module. Delivered live.</p>

            <ul className="space-y-3 mb-8">
              {[
                '3 live online days with Nicolas',
                '14 modules delivered live · Q&A on every block',
                'Repositioned CV, LinkedIn and narrative',
                'Real catering order practice on Day 2',
                'Mock operator interview + written feedback',
                'Placement roadmap and operator shortlist on Day 3',
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-[#C5A880] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
                  <span className="text-[15px] text-[#1A1A1A]">{line}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6 pb-6 border-b border-[#E5E0D8] mt-auto">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold mb-1">Next cohort</p>
                <p className="font-heading text-base text-[#0F0F0F]">{NEXT_LIVE.dates}</p>
              </div>
              <span className="text-xs uppercase tracking-[0.22em] text-[#C5A880] font-semibold">
                {SEATS_REMAINING}
              </span>
            </div>

            <a
              href={STRIPE_CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="academy-pricing-reserve-cta"
              onClick={() => window.gtag?.('event', 'checkout_clicked', { location: 'academy_pricing' })}
              className="btn-primary inline-flex items-center gap-2 w-full justify-center"
            >
              Reserve my seat — €600 <ArrowRight size={16} />
            </a>
            <TermsNote testId="academy-pricing-terms-note" className="mt-2 text-center" />

            <p className="mt-4 text-sm text-[#5A5A5A]">
              Not sure?{' '}
              <a
                href={DISCOVERY_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="academy-pricing-discovery-cta"
                className="text-[#C5A880] hover:underline font-medium"
              >
                Book a free discovery call →
              </a>
            </p>
          </div>

          {/* RIGHT — Bundle dark card */}
          <div data-testid="academy-bundle-card" className="border border-[#C5A880] bg-[#0F0F0F] text-[#FDFBF7] p-8 md:p-10 relative overflow-hidden flex flex-col">
            <div className="absolute top-0 right-0 bg-[#C5A880] text-[#0F0F0F] px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold">
              Save {BUNDLE_SAVINGS}
            </div>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold inline-block mb-4">Bundle · Complete path</span>
            <h3 className="font-heading text-2xl sm:text-3xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
              3-Day Online Training + 3-Month Mentorship
            </h3>

            <div className="flex flex-wrap items-baseline gap-3 mb-2">
              <span data-testid="academy-bundle-price" className="font-heading text-5xl text-[#FDFBF7] leading-none">{BUNDLE_PRICE}</span>
              <span className="text-lg text-[#9A9A9A] line-through">{BUNDLE_PRICE_STRIKE}</span>
            </div>
            <p className="text-xs text-[#9A9A9A] mb-6">
              Training €600 + Mentorship €2,200 — save {BUNDLE_SAVINGS} together. Or up to 24× with PayPal.
            </p>

            <ul className="space-y-3 mb-8">
              {[
                '3 live training days + 3-month mentorship',
                'Profile, CV & LinkedIn rebuild',
                'Access to our operator list + direct introductions',
                '6× 1:1 coaching calls + WhatsApp for 3 months',
                'Mock interviews & priority notice on new openings',
              ].map((h) => (
                <li key={h} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-[#C5A880] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
                  <span className="text-[15px] text-[#FDFBF7] leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col gap-3 pt-6 border-t border-[#C5A880]/30 mt-auto">
              <a
                href={DISCOVERY_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="academy-bundle-cta"
                onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'academy_bundle' })}
                className="inline-flex items-center justify-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-8 py-3.5 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
              >
                Book a Free Discovery Call <ArrowRight size={14} />
              </a>
              <a
                href={BUNDLE_CHECKOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="academy-bundle-buy-cta"
                onClick={() => window.gtag?.('event', 'checkout_clicked', { location: 'academy_bundle' })}
                className="inline-flex items-center justify-center gap-2 border border-[#C5A880] text-[#C5A880] px-8 py-3.5 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
              >
                Buy the bundle — {BUNDLE_PRICE} <ArrowRight size={14} />
              </a>
              <Link
                to="/mentorship"
                data-testid="academy-bundle-mentorship-link"
                className="inline-flex items-center gap-1.5 mt-1 text-xs uppercase tracking-[0.18em] text-[#C5A880] font-semibold hover:text-[#FDFBF7] transition-colors"
              >
                See the Mentorship <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section data-testid="academy-sessions" className="py-16 md:py-20 bg-[#FDFBF7]">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <span className="overline inline-block mb-4">Live cohort calendar</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-3 text-[#0F0F0F]">
            All upcoming cohorts.
          </h2>
          <p className="text-[#5A5A5A] leading-relaxed max-w-2xl">
            Each cohort is three consecutive live days. Seats are limited — reserved as they are booked.
          </p>
          <p className="text-xs text-[#5A5A5A] mt-3">
            €600 per seat · or up to 24× with PayPal.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-16 gap-y-1 bg-white border border-[#E5E0D8] p-6 md:p-8">
          {LIVE_SESSIONS.map((s, i) => (
            <div
              key={s.dates}
              data-testid={`academy-session-${i}`}
              className={`flex items-center justify-between gap-4 py-3 border-b border-[#E5E0D8] last:border-b-0 ${
                s.status === 'full' ? 'opacity-40' : ''
              }`}
            >
              <span
                className={`font-heading text-base ${
                  s.status === 'full' ? 'line-through text-[#9A9A9A]' : 'text-[#0F0F0F]'
                }`}
              >
                {s.dates}
              </span>
              <div className="flex items-center gap-4">
                <span
                  className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${
                    s.status === 'full'
                      ? 'text-[#9A9A9A]'
                      : s.status === 'last'
                      ? 'text-[#C5A880]'
                      : 'text-[#5A5A5A]'
                  }`}
                >
                  {STATUS_LABEL[s.status]}
                </span>
                {s.status !== 'full' && (
                  <a
                    href={STRIPE_CHECKOUT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`academy-session-${i}-reserve`}
                    onClick={() =>
                      window.gtag?.('event', 'checkout_clicked', {
                        location: 'academy_sessions',
                        cohort: s.dates,
                      })
                    }
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold border border-[#0F0F0F] px-3 py-1.5 text-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-[#FDFBF7] transition-all"
                  >
                    Reserve <ArrowRight size={10} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ============================================================ */}
    {/* FINAL CTA                                                      */}
    {/* ============================================================ */}
    {/* ============================================================ */}
    {/* OBJECTION FAQ                                                  */}
    {/* ============================================================ */}
    <section data-testid="academy-faq" className="py-16 md:py-20 bg-[#FDFBF7]">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <span className="overline inline-block mb-4">Objections, answered directly</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#0F0F0F]">
            The questions candidates actually ask.
          </h2>
        </div>

        <div className="space-y-2">
          {OBJECTION_FAQ.map((f, i) => (
            <details
              key={f.q}
              data-testid={`academy-faq-${i}`}
              className="group border-b border-[#E5E0D8] py-5"
            >
              <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                <span className="font-heading text-lg text-[#0F0F0F] leading-snug">{f.q}</span>
                <span className="text-[#C5A880] font-heading text-2xl leading-none flex-shrink-0 group-open:rotate-45 transition-transform">
                  +
                </span>
              </summary>
              <p className="mt-4 text-[#5A5A5A] leading-relaxed text-[15px]">{f.a}</p>
              {f.discoveryLink && (
                <a
                  href={DISCOVERY_CALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`academy-faq-${i}-discovery-link`}
                  onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'academy_faq' })}
                  className="inline-flex items-center gap-1.5 mt-3 text-xs uppercase tracking-[0.18em] text-[#C5A880] font-semibold hover:text-[#0F0F0F] transition-colors"
                >
                  Book a free discovery call <ArrowRight size={12} />
                </a>
              )}
            </details>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="academy-final-cta" className="section-dark py-20 md:py-24">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <Sparkles size={22} className="text-[#C5A880] mx-auto mb-4" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#C5A880] inline-block mb-4">
          Ready to move?
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-4 text-[#FDFBF7]">
          Next cohort — {NEXT_LIVE.dates}.
        </h2>
        <p className="text-[#9A9A9A] leading-relaxed mb-4">{SEATS_REMAINING}. €600 or up to 24× payments with PayPal.</p>
        <p className="text-[#9A9A9A] leading-relaxed mb-10">
          Not ready to reserve? Book a free 45-minute discovery call and we&apos;ll walk your profile through together.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <a
            href={DISCOVERY_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="academy-final-discovery-cta"
            onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'academy_final' })}
            className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
          >
            Book a Free Discovery Call <ArrowRight size={16} />
          </a>
          <a
            href={STRIPE_CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="academy-final-reserve-cta"
            onClick={() => window.gtag?.('event', 'checkout_clicked', { location: 'academy_final' })}
            className="inline-flex items-center gap-2 border border-[#C5A880] text-[#C5A880] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
          >
            Reserve my seat — €600 <ArrowRight size={16} />
          </a>
        </div>
        <TermsNote testId="academy-final-terms-note" className="mt-4" dark />
      </div>
    </section>    </div>
  );
};

export default AcademyPage;
